import { router } from "@inertiajs/react"
import { type ChangeEvent, useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { brandSettingsLinkSharingOgImagePath } from "@/routes"

/**
 * OKS-305 — Brand OG image upload + remove widget for the Brand
 * Settings → Link sharing page.
 *
 * Modelled directly on `<BrandCoverImageUploader />` (OKS-304) — same
 * affordance shape, same RevokeDialog precedent for the destructive
 * remove confirm. Differences:
 *   - 1.91:1 preview band (vs. 3:1 cover) — OG canonical 1200×630.
 *   - `onFileChange` callback so the parent page can pipe the picked
 *     File into the live preview tile (`<LinkSharingPreviewTile />`).
 *     Cover uploader doesn't need this — its own band IS the preview.
 *   - Hidden file input is `name="brand[og_image]"` matching
 *     `BrandSettingsLinkSharingsController#link_sharing_params`.
 *
 * Affordances (UX Spec §2.1):
 *   - Replace button → opens the hidden file picker; on pick, swaps
 *     the preview to the picked file via `URL.createObjectURL` AND
 *     notifies the parent via `onFileChange`.
 *   - Remove button (gated on `currentUrl` truthy) → opens a
 *     destructive `<Dialog>` with Cancel as primary-focus (mirrors
 *     RevokeDialog idiom from `Invitations/Index.tsx`); confirm fires
 *     `router.delete(brandSettingsLinkSharingOgImagePath())`.
 *   - Read-only mode (Brand Member tier): hides Replace + Remove,
 *     leaves the preview band visible.
 *
 * Brand isolation: the `currentUrl` prop comes from server-rendered
 * Inertia props which are derived from `Current.brand` only. The
 * widget never accepts a brand id.
 *
 * OKS-366 — Orphan-blob graceful degradation: the `<img>` `onError`
 * fires when the persisted blob exists in the DB but the underlying
 * S3 object is missing (historical residue from the pre-fix Disk-era
 * production deploys). On error the preview swaps to the empty-state
 * placeholder, matching the `BrandLogo` (OKS-303) / `BrandCoverBand`
 * (OKS-304) idioms. Silent degradation per UX Spec §3 / §4 — no
 * toast, no banner; the user can re-upload via Replace.
 */
export interface BrandOgImageUploaderProps {
  /** Current OG image URL from Inertia props (`brand.og_image_url`)
   *  — null when no OG image attached. */
  currentUrl: string | null
  /** Brand display name — used in the preview band's `alt` attr. */
  brandName: string
  /** Wire-format strong-params input name. Default
   *  `brand[og_image]` matches the controller's permit list. */
  inputName?: string
  /** Disable affordances for Brand Member / Agency Member read-only tiers. */
  readOnly?: boolean
  /** Callback invoked when the file picker selects a new file (or is
   *  cleared). Parent uses it to drive the live preview tile so the
   *  admin sees the picked-but-not-saved image before submitting. */
  onFileChange?: (file: File | null) => void
  /** Test-id wrapper. */
  testId?: string
}

export function BrandOgImageUploader({
  currentUrl,
  brandName,
  inputName = "brand[og_image]",
  readOnly = false,
  onFileChange,
  testId = "brand-og-image-uploader",
}: BrandOgImageUploaderProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const helperId = useId()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [removeProcessing, setRemoveProcessing] = useState(false)
  // OKS-366 — when the `<img>` fails to load (orphan blob row: AS
  // row exists but the underlying S3 object is missing — historical
  // residue from the pre-fix Disk deploys, or any future S3 purge),
  // swap the preview to the empty-state placeholder rather than
  // letting the browser's broken-image icon render. Mirrors the
  // `BrandLogo onError → monogram` (OKS-303) + `BrandCoverBand
  // onError → dark band` (OKS-304) idioms. Reset on `previewUrl` or
  // `currentUrl` change so picking a new file after a render error
  // re-attempts the load against the new source.
  const [previewBroken, setPreviewBroken] = useState(false)

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    if (!file) {
      setPreviewUrl(null)
      onFileChange?.(null)
      return
    }
    setPreviewUrl(URL.createObjectURL(file))
    onFileChange?.(file)
  }

  const onClear = () => {
    setPreviewUrl(null)
    onFileChange?.(null)
    if (inputRef.current) inputRef.current.value = ""
  }

  // Replace button delegates to the hidden file input. Same idiom as
  // every other "trigger a hidden input" widget on shadcn.
  const onReplaceClick = () => {
    inputRef.current?.click()
  }

  const handleRemove = () => {
    setRemoveProcessing(true)
    router.delete(brandSettingsLinkSharingOgImagePath(), {
      preserveScroll: true,
      onFinish: () => {
        setRemoveProcessing(false)
        setRemoveOpen(false)
        // Clear any client-side preview so the post-delete page reload
        // starts from a clean state.
        onClear()
      },
    })
  }

  // Preview shows: the picked-but-not-yet-saved file > the persisted
  // OG URL > a dark fallback band (no image yet — Resolver will fall
  // back to cover_image or platform default on the actual landing).
  const displayUrl = previewUrl ?? currentUrl

  // OKS-366 — clear the `previewBroken` flag when the displayed URL
  // changes (admin picked a new file after a load failure, OR the
  // server-rendered `currentUrl` flipped on a server-driven reload).
  // Without this, a transient broken state would persist across an
  // otherwise-recoverable swap. Reset by URL identity, not file
  // identity — both branches (local object URL + server URL) flow
  // through `displayUrl`.
  //
  // Uses the React-docs "Adjusting state during render" pattern
  // (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // instead of `useEffect(() => setX(...), [dep])` — which is the
  // canonical `react-hooks/set-state-in-effect` lint anti-pattern.
  // The synchronous reset happens BEFORE the next render reads
  // `previewBroken`, so no extra render cycle is queued.
  const [lastDisplayUrl, setLastDisplayUrl] = useState(displayUrl)
  if (displayUrl !== lastDisplayUrl) {
    setLastDisplayUrl(displayUrl)
    setPreviewBroken(false)
  }

  const showImage = displayUrl !== null && !previewBroken

  return (
    <div data-testid={testId} className="space-y-3">
      <Label htmlFor={inputId}>
        {t("settings.brand.link_sharing.og_image_label")}
      </Label>

      {/* 1.91:1 (1200×630) preview band. Mirrors the OG canonical
          aspect so the admin gauges crop fidelity before saving. */}
      <div
        data-testid={`${testId}-preview`}
        className={cn(
          "bg-muted relative w-full max-w-[320px] overflow-hidden rounded-lg border",
          "aspect-[1200/630]",
        )}
      >
        {showImage ? (
          <img
            src={displayUrl ?? undefined}
            alt={t("settings.brand.link_sharing.og_image_label")}
            className="h-full w-full object-cover"
            data-testid={`${testId}-preview-image`}
            // OKS-366 — fallback to the empty-state placeholder when
            // the underlying blob is missing (e.g. orphan AS row left
            // over from the pre-fix Disk-era deploys). Silent
            // degradation per UX Spec §3 / §4 (no toast, no banner).
            onError={() => setPreviewBroken(true)}
          />
        ) : (
          <div
            className="text-muted-foreground flex h-full w-full items-center justify-center text-xs"
            data-testid={`${testId}-preview-empty`}
          >
            {brandName}
          </div>
        )}
      </div>

      <p id={helperId} className="text-muted-foreground max-w-[420px] text-xs">
        {t("settings.brand.link_sharing.og_image_helper")}
      </p>

      {/* Hidden file input is ALWAYS in the DOM (read-only or not) so
          the parent Inertia `<Form>` auto-detects multipart. Read-only
          mode hides the affordance buttons; the user can't reach the
          input — and the form-level Save button is also hidden, so the
          input's presence is harmless. */}
      {readOnly ? null : (
        <Input
          ref={inputRef}
          id={inputId}
          type="file"
          name={inputName}
          accept="image/jpeg,image/png,image/webp"
          onChange={onPick}
          aria-describedby={helperId}
          aria-label={t("settings.brand.link_sharing.og_image_label")}
          className="sr-only"
          data-testid={`${testId}-input`}
        />
      )}

      {readOnly ? null : (
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onReplaceClick}
            data-testid={`${testId}-replace`}
            className="min-h-11"
          >
            {t("settings.brand.link_sharing.og_image_replace")}
          </Button>

          {previewUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              data-testid={`${testId}-clear`}
              className="text-muted-foreground sm:self-center"
            >
              {t("settings.brand.link_sharing.og_image_clear")}
            </Button>
          ) : null}

          {currentUrl ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRemoveOpen(true)}
              data-testid={`${testId}-remove`}
              className="min-h-11 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              {t("settings.brand.link_sharing.og_image_remove")}
            </Button>
          ) : null}
        </div>
      )}

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent data-testid={`${testId}-remove-dialog`}>
          <DialogTitle>
            {t("settings.brand.link_sharing.og_image_remove_confirm_title")}
          </DialogTitle>
          <DialogDescription>
            {t("settings.brand.link_sharing.og_image_remove_confirm_body")}
          </DialogDescription>
          <DialogFooter>
            {/* Cancel is primary-focus per UX Spec §6 / RevokeDialog
                  precedent — destructive actions are NEVER the default
                  focus target of a Dialog. */}
            <DialogClose
              render={
                <Button
                  variant="secondary"
                  autoFocus
                  disabled={removeProcessing}
                  data-testid={`${testId}-remove-cancel`}
                >
                  {t(
                    "settings.brand.link_sharing.og_image_remove_confirm_cancel",
                  )}
                </Button>
              }
            />
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={removeProcessing}
              data-testid={`${testId}-remove-confirm`}
            >
              {t("settings.brand.link_sharing.og_image_remove_confirm_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default BrandOgImageUploader
